// In-app camera: photo or video with live filters, front/back switch and a review step.
// The captured file goes to the same place a picked file goes (the composer or a Story),
// so upload, safety checks and publication stay exactly as they are.
(function (root, factory) {
  const api = factory();
  if (typeof module === "object" && module.exports) module.exports = api;
  if (root) root.LouderrCamera = api;
})(typeof window === "undefined" ? null : window, function () {
  "use strict";

  const FILTERS = Object.freeze([
    { id: "none", label: "Original", css: "none" },
    { id: "warm", label: "Warm", css: "sepia(0.22) saturate(1.2) hue-rotate(-8deg)" },
    { id: "cool", label: "Cool", css: "saturate(1.08) hue-rotate(12deg) brightness(1.02)" },
    { id: "mono", label: "Mono", css: "grayscale(1) contrast(1.12)" },
    { id: "vivid", label: "Vivid", css: "saturate(1.45) contrast(1.08)" },
    { id: "fade", label: "Fade", css: "contrast(0.86) brightness(1.08) saturate(0.82)" },
  ]);
  const VIDEO_TYPES = Object.freeze([
    "video/mp4;codecs=avc1.42E01E,mp4a.40.2",
    "video/mp4",
    "video/webm;codecs=vp9,opus",
    "video/webm;codecs=vp8,opus",
    "video/webm",
  ]);

  function filterCss(id) {
    return (FILTERS.find(filter => filter.id === id) || FILTERS[0]).css;
  }

  function pickVideoType(isTypeSupported) {
    if (typeof isTypeSupported !== "function") return "";
    return VIDEO_TYPES.find(type => { try { return isTypeSupported(type); } catch (_) { return false; } }) || "";
  }

  function pad(value) { return String(value).padStart(2, "0"); }

  function fileName(kind, mime, now = new Date()) {
    const stamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
    const extension = kind === "photo" ? "jpg" : String(mime || "").startsWith("video/mp4") ? "mp4" : "webm";
    return `louderr-${kind}-${stamp}.${extension}`;
  }

  function clampSeconds(value, fallback = 60) {
    const number = Number(value);
    return Number.isFinite(number) && number >= 3 ? Math.min(180, Math.floor(number)) : fallback;
  }

  function captureLandscape(view) {
    // Desktop cameras also start in portrait. On phones, use screen orientation
    // rather than the viewport, which can change when browser controls appear.
    const mobile = Number(view?.navigator?.maxTouchPoints) > 0 || typeof view?.orientation === "number";
    if (!mobile) return false;
    const type = view?.screen?.orientation?.type;
    if (type?.startsWith("landscape")) return true;
    if (type?.startsWith("portrait")) {
      // A few mobile browsers keep the ScreenOrientation value stale while
      // rotating an in-app camera. A clearly landscape viewport is still a
      // reliable signal for the requested phone orientation; retain the
      // ScreenOrientation value for portrait viewports and keyboard changes.
      const width = Number(view?.innerWidth), height = Number(view?.innerHeight);
      const media = view?.matchMedia?.("(orientation: landscape)");
      if (Number.isFinite(width) && Number.isFinite(height) && width > height
          && media?.matches !== false) return true;
      return false;
    }
    if (typeof view?.orientation === "number") return Math.abs(view.orientation) === 90;
    return Boolean(view?.matchMedia?.("(orientation: landscape)").matches);
  }

  function captureGeometry(width, height, landscape, maxLongEdge = Infinity) {
    if (!(Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0)) return null;
    const unitX = landscape ? 16 : 9, unitY = landscape ? 9 : 16;
    const ratio = unitX / unitY;
    const cropWidth = Math.min(width, height * ratio);
    const cropHeight = Math.min(height, width / ratio);
    // Exact ratio, even encoder dimensions and no upscaling. Only video is capped.
    const units = Math.floor(Math.min(cropWidth / unitX, cropHeight / unitY, maxLongEdge / 16) / 2) * 2;
    if (units < 2) return null;
    return { x: (width - cropWidth) / 2, y: (height - cropHeight) / 2,
      cropWidth, cropHeight, width: units * unitX, height: units * unitY };
  }

  function errorMessage(error) {
    const name = error?.name || "";
    if (name === "NotAllowedError" || name === "SecurityError") {
      return "Camera access is off for LOUDERR. Allow it in your browser settings, or choose a file instead.";
    }
    if (name === "NotFoundError" || name === "OverconstrainedError") return "No camera was found on this device.";
    if (name === "NotReadableError") return "The camera is busy in another app. Close it and try again.";
    return "The camera could not start. Choose a file instead.";
  }

  // Pixel fallback for browsers without CanvasRenderingContext2D.filter (Safari).
  function applyPixelFilter(data, id) {
    const pixels = data.data;
    for (let index = 0; index < pixels.length; index += 4) {
      let red = pixels[index], green = pixels[index + 1], blue = pixels[index + 2];
      if (id === "mono") {
        const grey = Math.min(255, Math.max(0, (0.299 * red + 0.587 * green + 0.114 * blue - 128) * 1.12 + 128));
        red = green = blue = grey;
      } else if (id === "warm") {
        red = red * 1.08 + 10; green = green * 1.02 + 4; blue = blue * 0.9;
      } else if (id === "cool") {
        red = red * 0.94; green = green * 1.01; blue = blue * 1.08 + 8;
      } else if (id === "vivid") {
        const grey = 0.299 * red + 0.587 * green + 0.114 * blue;
        red = grey + (red - grey) * 1.45; green = grey + (green - grey) * 1.45; blue = grey + (blue - grey) * 1.45;
      } else if (id === "fade") {
        red = (red - 128) * 0.86 + 138; green = (green - 128) * 0.86 + 138; blue = (blue - 128) * 0.86 + 136;
      }
      pixels[index] = Math.max(0, Math.min(255, red));
      pixels[index + 1] = Math.max(0, Math.min(255, green));
      pixels[index + 2] = Math.max(0, Math.min(255, blue));
    }
    return data;
  }

  function canvasFilterSupported(context) {
    if (!context || !("filter" in context)) return false;
    context.filter = "grayscale(1)";
    const supported = context.filter === "grayscale(1)";
    context.filter = "none";
    return supported;
  }

  let active = null;

  function open(options = {}) {
    if (active) active.close();
    const doc = options.document || document;
    const view = doc.defaultView || (typeof window !== "undefined" ? window : null);
    const media = options.mediaDevices || (typeof navigator !== "undefined" ? navigator.mediaDevices : null);
    const recorderClass = options.MediaRecorder || (typeof MediaRecorder !== "undefined" ? MediaRecorder : null);
    const maxSeconds = clampSeconds(options.maxVideoSeconds, 60);
    const allowVideo = options.allowVideo !== false && Boolean(recorderClass);
    const node = (tag, className = "", text = "") => {
      const element = doc.createElement(tag);
      if (className) element.className = className;
      if (text) element.textContent = text;
      return element;
    };
    const button = (label, className, handler, iconName = "") => {
      const element = node("button", className);
      element.type = "button";
      element.setAttribute("aria-label", label);
      if (iconName) {
        element.innerHTML = `<svg aria-hidden="true"><use href="#icon-${iconName}"></use></svg>`;
      } else element.textContent = label;
      element.addEventListener("click", handler);
      return element;
    };

    // A modal <dialog>: the composer that opens the camera is modal, and only a later
    // modal dialog stacks above it.
    const shell = node("dialog", "lc-camera");
    shell.setAttribute("aria-label", options.title || "Camera");
    const stage = node("div", "lc-camera-stage");
    const frame = node("div", "lc-camera-frame");
    const video = node("video", "lc-camera-video");
    video.setAttribute("playsinline", "");
    video.muted = true;
    video.autoplay = true;
    const review = node("div", "lc-camera-review");
    review.hidden = true;
    const status = node("p", "lc-camera-status");
    status.setAttribute("role", "status");
    status.setAttribute("aria-live", "polite");
    const timer = node("span", "lc-camera-timer");
    timer.hidden = true;
    frame.append(video, review);
    stage.append(frame, timer);

    const top = node("div", "lc-camera-top");
    const closeButton = button("Close camera", "lc-round", () => close(), "x");
    const flipButton = button("Switch camera", "lc-round", () => switchCamera(), "refresh");
    top.append(closeButton, flipButton);

    const filters = node("div", "lc-camera-filters");
    filters.setAttribute("role", "radiogroup");
    filters.setAttribute("aria-label", "Filter");
    let filterId = "none";
    for (const filter of FILTERS) {
      const chip = node("button", "lc-filter", filter.label);
      chip.type = "button";
      chip.setAttribute("role", "radio");
      chip.dataset.filter = filter.id;
      chip.addEventListener("click", () => setFilter(filter.id));
      filters.append(chip);
    }

    const modes = node("div", "lc-camera-modes");
    let mode = options.mode === "video" && allowVideo ? "video" : "photo";
    const photoMode = node("button", "lc-mode", "Photo");
    const videoMode = node("button", "lc-mode", "Video");
    for (const [element, value] of [[photoMode, "photo"], [videoMode, "video"]]) {
      element.type = "button";
      element.addEventListener("click", () => setMode(value));
    }
    videoMode.hidden = !allowVideo;
    modes.append(photoMode, videoMode);

    const bottom = node("div", "lc-camera-bottom");
    const library = button("Choose from library", "lc-round lc-library", () => { close(); options.onFallback?.(); }, "image");
    const shutter = button("Take photo", "lc-shutter", () => shutterPressed());
    const spacer = node("span", "lc-round lc-spacer");
    bottom.append(library, shutter, spacer);

    const reviewBar = node("div", "lc-camera-review-bar");
    reviewBar.hidden = true;
    const retake = node("button", "secondary-button", "Retake");
    retake.type = "button";
    const use = node("button", "publish-button", options.useLabel || "Use this");
    use.type = "button";
    reviewBar.append(retake, use);

    shell.append(stage, top, status, filters, modes, bottom, reviewBar);
    doc.body.append(shell);
    if (typeof shell.showModal === "function") shell.showModal();
    else shell.setAttribute("open", "");

    let stream = null;
    let streamEndCleanup = null;
    let captureNotice = "";
    let previewReady = false;
    let streamRequest = 0;
    let facing = options.facing === "user" ? "user" : "environment";
    let recorder = null;
    let drawHandle = 0;
    let tickHandle = 0;
    let startedAt = 0;
    let captured = null;
    let previewUrl = "";
    let closed = false;
    let landscape = captureLandscape(view);
    let photoPending = false;
    let photoAttempt = null;
    let photoTimer = null;
    let recordingStream = null;
    let recordingFailure = null;
    let stopHandle = null;
    const previousFocus = doc.activeElement;

    function say(text) { status.textContent = text || ""; }

    function showStartError(message) {
      say(message);
      status.append(button("Try camera again", "lc-camera-retry", () => { void startStream(); }));
    }

    function updateOrientation() {
      if (closed || captured || recorder || photoPending) return;
      landscape = captureLandscape(view);
      shell.style.setProperty("--lc-frame-ratio", landscape ? "16 / 9" : "9 / 16");
      shell.dataset.captureOrientation = landscape ? "landscape" : "portrait";
    }

    function readyToCapture() {
      return previewReady && stream && stream.getTracks().every(track => track.readyState !== "ended")
        && video.readyState >= 2 && video.videoWidth > 0 && video.videoHeight > 0;
    }

    function setFilter(id) {
      filterId = FILTERS.some(filter => filter.id === id) ? id : "none";
      video.style.filter = filterCss(filterId);
      for (const chip of filters.children) chip.setAttribute("aria-checked", String(chip.dataset.filter === filterId));
    }

    function setMode(value) {
      if (recorder || photoPending) return;
      mode = value === "video" && allowVideo ? "video" : "photo";
      photoMode.setAttribute("aria-pressed", String(mode === "photo"));
      videoMode.setAttribute("aria-pressed", String(mode === "video"));
      shutter.setAttribute("aria-label", mode === "video" ? "Start recording" : "Take photo");
      shutter.classList.toggle("is-video", mode === "video");
      void startStream();
    }

    function stopTracks() {
      previewReady = false;
      streamEndCleanup?.();
      streamEndCleanup = null;
      const previous = stream;
      stream = null;
      video.srcObject = null;
      if (previous) for (const track of previous.getTracks()) track.stop();
    }

    function handleStreamEnded(endedStream) {
      if (closed || stream !== endedStream) return;
      ++streamRequest;
      previewReady = false;
      if (recorder) {
        captureNotice = "The camera or microphone was interrupted. Review the saved video before using it.";
        stopTracks();
        stopRecording();
      } else if (photoPending) {
        // The frame has already been drawn; let its pending JPEG finish.
        stopTracks();
      } else {
        stopTracks();
        showStartError("The camera or microphone stopped. Try again or choose a file from your library.");
      }
    }

    async function startStream() {
      if (closed) return;
      // Mode and lens switches can overlap; only the newest request may keep its stream, and one
      // that arrives after close or after a newer request is stopped at once.
      const request = ++streamRequest;
      stopTracks();
      captureNotice = "";
      updateOrientation();
      if (!media?.getUserMedia) { say("This browser cannot open the camera. Choose a file instead."); return; }
      say("Starting the camera…");
      let openingPreview = false;
      try {
        const next = await media.getUserMedia({
          video: { facingMode: facing, width: { ideal: landscape ? 1920 : 1080 },
            height: { ideal: landscape ? 1080 : 1920 }, aspectRatio: { ideal: landscape ? 16 / 9 : 9 / 16 } },
          audio: mode === "video",
        });
        if (closed || request !== streamRequest) {
          for (const track of next.getTracks()) track.stop();
          return;
        }
        stopTracks();
        stream = next;
        video.srcObject = stream;
        const ended = () => handleStreamEnded(next);
        for (const track of next.getTracks()) track.addEventListener?.("ended", ended);
        streamEndCleanup = () => {
          for (const track of next.getTracks()) track.removeEventListener?.("ended", ended);
        };
        if (next.getTracks().some(track => track.readyState === "ended")) { ended(); return; }
        // Photos bake in the selfie mirror. Video keeps the camera orientation;
        // show that same orientation before recording so review cannot flip it.
        video.classList.toggle("is-mirrored", facing === "user" && mode === "photo");
        openingPreview = true;
        await video.play();
        if (closed || request !== streamRequest) return;
        previewReady = true;
        say("");
      } catch (error) {
        if (!closed && request === streamRequest) {
          stopTracks();
          showStartError(openingPreview ? "The camera preview could not start. Try again or choose a file from your library." : errorMessage(error));
        }
      }
    }

    function switchCamera() {
      if (recorder || photoPending) return;
      facing = facing === "user" ? "environment" : "user";
      void startStream();
    }

    function frameCanvas() {
      const geometry = captureGeometry(video.videoWidth, video.videoHeight, landscape, mode === "video" ? 1920 : Infinity);
      if (!geometry) return null;
      const canvas = node("canvas");
      canvas.width = geometry.width;
      canvas.height = geometry.height;
      return canvas;
    }

    function drawFrame(context, canvas, useFilter) {
      context.save();
      if (useFilter) context.filter = filterCss(filterId);
      if (facing === "user" && mode === "photo") { context.translate(canvas.width, 0); context.scale(-1, 1); }
      const geometry = captureGeometry(video.videoWidth, video.videoHeight, canvas.width > canvas.height);
      if (geometry) context.drawImage(video, geometry.x, geometry.y, geometry.cropWidth, geometry.cropHeight,
        0, 0, canvas.width, canvas.height);
      context.restore();
    }

    function releasePhotoCapture() {
      clearTimeout(photoTimer);
      photoTimer = null;
      photoAttempt = null;
      photoPending = false;
      shutter.disabled = false;
      shutter.setAttribute("aria-label", "Take photo");
    }

    function takePhoto() {
      if (closed || captured || photoPending) return;
      if (!readyToCapture()) { say("The camera is not ready yet."); return; }
      updateOrientation();
      const canvas = frameCanvas();
      if (!canvas) { say("The camera is not ready yet."); return; }
      const context = canvas.getContext("2d");
      if (!context) { say("The photo could not be captured."); return; }
      photoPending = true;
      const attempt = {};
      photoAttempt = attempt;
      shutter.disabled = true;
      shutter.setAttribute("aria-label", "Saving photo");
      say("Saving your photo…");
      const fail = () => {
        if (photoAttempt !== attempt || closed) return;
        releasePhotoCapture();
        updateOrientation();
        const message = "The photo could not be saved. Try again or choose a photo from your library.";
        if (!stream) showStartError(message);
        else say(message);
      };
      photoTimer = setTimeout(fail, 10000);
      try {
        const native = canvasFilterSupported(context);
        drawFrame(context, canvas, native);
        if (!native && filterId !== "none") {
          context.putImageData(applyPixelFilter(context.getImageData(0, 0, canvas.width, canvas.height), filterId), 0, 0);
        }
        canvas.toBlob(blob => {
          if (closed || photoAttempt !== attempt) return;
          if (!blob?.size) { fail(); return; }
          let file;
          try { file = new File([blob], fileName("photo", "image/jpeg"), { type: "image/jpeg" }); }
          catch (_) { fail(); return; }
          releasePhotoCapture();
          showReview(file);
        }, "image/jpeg", 0.92);
      } catch (_) { fail(); }
    }

    function startRecording() {
      if (!stream || !recorderClass) { say("Recording is not available in this browser."); return; }
      if (!readyToCapture()) { say("The camera is not ready yet."); return; }
      updateOrientation();
      const mime = pickVideoType(recorderClass.isTypeSupported?.bind(recorderClass));
      const canvas = frameCanvas();
      if (!canvas) { say("The camera is not ready yet."); return; }
      const context = canvas.getContext("2d");
      if (!context || typeof canvas.captureStream !== "function") {
        say("This browser cannot record this format. Choose a video from your library instead.");
        return;
      }
      const nativeFilter = canvasFilterSupported(context);
      if (filterId !== "none" && !nativeFilter) {
        setFilter("none");
        say("Video filters are unavailable in this browser. Recording with Original.");
      } else say("");
      const chunks = [];
      try {
        drawFrame(context, canvas, nativeFilter);
        recordingStream = canvas.captureStream(30);
        for (const track of stream.getAudioTracks()) recordingStream.addTrack(track);
        try {
          recorder = new recorderClass(recordingStream, mime ? { mimeType: mime, videoBitsPerSecond: 4_000_000 } : undefined);
        } catch (_) {
          recorder = new recorderClass(recordingStream);
        }
      } catch (_) {
        releaseRecording();
        say("Recording could not start. Try again or choose a video from your library.");
        return;
      }
      const take = recorder;
      function failRecording(message) {
        if (recorder !== take) return;
        // Invalidate first: stop/error/data events from this take may arrive
        // after the user has already started another recording.
        recorder = null;
        if (take.state !== "inactive") { try { take.stop(); } catch (_) { /* recorder failed */ } }
        releaseRecording();
        timer.hidden = true;
        shutter.classList.remove("is-recording");
        shutter.setAttribute("aria-label", "Start recording");
        chunks.length = 0;
        updateOrientation();
        if (!closed) {
          if (!stream) showStartError(message);
          else say(message);
        }
      }
      recordingFailure = failRecording;
      take.addEventListener("error", () => {
        failRecording("Recording was interrupted and could not be saved. Try again or choose a video from your library.");
      });
      take.addEventListener("dataavailable", event => {
        if (recorder === take && !closed && event.data?.size) chunks.push(event.data);
      });
      take.addEventListener("stop", () => {
        if (recorder !== take) return;
        releaseRecording();
        timer.hidden = true;
        shutter.classList.remove("is-recording");
        shutter.setAttribute("aria-label", "Start recording");
        const type = (take.mimeType || mime || "video/webm").split(";")[0];
        recorder = null;
        if (closed) return;
        if (!chunks.length) {
          updateOrientation();
          if (!stream) showStartError("No video was saved. Try recording again.");
          else say("No video was saved. Try recording again.");
          return;
        }
        const blob = new Blob(chunks, { type });
        showReview(new File([blob], fileName("video", type), { type }));
      });
      try { take.start(250); } catch (_) {
        failRecording("Recording could not start. Try again or choose a video from your library.");
        return;
      }
      let lastDrawAt = 0;
      const draw = now => {
        if (closed || recorder !== take || take.state !== "recording") return;
        try {
          if (now - lastDrawAt >= 1000 / 30) {
            drawFrame(context, canvas, nativeFilter);
            lastDrawAt = now - ((now - lastDrawAt) % (1000 / 30));
          }
        } catch (_) {
          failRecording("The camera image was interrupted and the video could not be saved. Try recording again.");
          return;
        }
        drawHandle = requestAnimationFrame(draw);
      };
      drawHandle = requestAnimationFrame(draw);
      for (const chip of filters.children) chip.disabled = true;
      startedAt = Date.now();
      shutter.classList.add("is-recording");
      shutter.setAttribute("aria-label", "Stop recording");
      timer.hidden = false;
      const tick = () => {
        const seconds = Math.floor((Date.now() - startedAt) / 1000);
        timer.textContent = `${Math.floor(seconds / 60)}:${pad(seconds % 60)} / ${Math.floor(maxSeconds / 60)}:${pad(maxSeconds % 60)}`;
        if (seconds >= maxSeconds) stopRecording();
      };
      tick();
      tickHandle = setInterval(tick, 250);
    }

    function releaseRecording() {
      cancelAnimationFrame(drawHandle);
      clearInterval(tickHandle);
      clearTimeout(stopHandle);
      stopHandle = null;
      recordingFailure = null;
      shutter.disabled = false;
      // Audio belongs to the camera stream; only dispose the generated video track here.
      if (recordingStream) for (const track of recordingStream.getVideoTracks()) track.stop();
      recordingStream = null;
      for (const chip of filters.children) chip.disabled = false;
    }

    function stopRecording() {
      const take = recorder, fail = recordingFailure;
      if (!take || !fail || stopHandle !== null) return;
      clearInterval(tickHandle);
      cancelAnimationFrame(drawHandle);
      shutter.disabled = true;
      shutter.setAttribute("aria-label", "Saving recording");
      say("Saving your video…");
      // Native final data and stop may arrive after state becomes inactive.
      // Keep this take alive for them, but never leave capture locked forever.
      stopHandle = setTimeout(() => {
        if (recorder === take) fail("The video could not finish saving. Try recording again or choose a video from your library.");
      }, 10000);
      try { if (take.state !== "inactive") take.stop(); }
      catch (_) { fail("The video could not finish saving. Try recording again or choose a video from your library."); }
    }

    function shutterPressed() {
      if (mode === "photo") takePhoto();
      else if (recorder) stopRecording();
      else startRecording();
    }

    function clearReview() {
      for (const preview of review.querySelectorAll("video, img")) {
        try {
          if (preview.tagName === "VIDEO") preview.pause();
          preview.removeAttribute("src");
          if (preview.tagName === "VIDEO") preview.load();
        } catch (_) { /* A detached player may already be torn down. */ }
      }
      review.replaceChildren();
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      previewUrl = "";
    }

    function showReview(file) {
      clearReview();
      captured = file;
      previewUrl = URL.createObjectURL(file);
      const preview = file.type.startsWith("video/") ? node("video") : node("img");
      preview.src = previewUrl;
      if (preview.tagName === "VIDEO") { preview.controls = true; preview.setAttribute("playsinline", ""); preview.loop = true; }
      else preview.alt = "The photo you just took";
      review.append(preview);
      review.hidden = false;
      video.hidden = true;
      reviewBar.hidden = false;
      say(captureNotice);
      captureNotice = "";
      for (const part of [filters, modes, bottom, flipButton]) part.hidden = true;
      stopTracks();
      use.focus();
    }

    retake.addEventListener("click", () => {
      captured = null;
      review.hidden = true;
      clearReview();
      video.hidden = false;
      reviewBar.hidden = true;
      for (const part of [filters, modes, bottom, flipButton]) part.hidden = false;
      videoMode.hidden = !allowVideo;
      void startStream();
    });

    use.addEventListener("click", () => {
      const file = captured;
      close();
      if (file) options.onCapture?.(file);
    });

    shell.addEventListener("cancel", event => { event.preventDefault(); close(); });

    function close() {
      if (closed) return;
      closed = true;
      releasePhotoCapture();
      if (recorder && recorder.state !== "inactive") { try { recorder.stop(); } catch (_) { /* already stopped */ } }
      releaseRecording();
      view?.screen?.orientation?.removeEventListener?.("change", updateOrientation);
      view?.removeEventListener?.("orientationchange", updateOrientation);
      view?.removeEventListener?.("resize", updateOrientation);
      stopTracks();
      clearReview();
      captured = null;
      if (shell.open && typeof shell.close === "function") shell.close();
      shell.remove();
      if (active?.shell === shell) active = null;
      options.onClose?.();
      if (previousFocus && typeof previousFocus.focus === "function") previousFocus.focus();
    }

    view?.screen?.orientation?.addEventListener?.("change", updateOrientation);
    view?.addEventListener?.("orientationchange", updateOrientation);
    view?.addEventListener?.("resize", updateOrientation);
    updateOrientation();
    setFilter("none");
    setMode(mode);
    closeButton.focus();
    active = { shell, close };
    return active;
  }

  return {
    open,
    close: () => active?.close(),
    FILTERS,
    _test: { filterCss, pickVideoType, fileName, clampSeconds, errorMessage, applyPixelFilter, captureLandscape, captureGeometry },
  };
});
